import { createContext, useEffect, ReactNode, useContext, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { LoginContextType, ViewName } from "./LoginView.types";

import Forgot from "../../components/Forgot/index.tsx";
import Login from "../../components/Login/index.tsx";
import OtpPassword from "../../components/OtpPassword/index.tsx";
import OtpRegister from "../../components/OtpRegister/index.tsx";
import PasswordReset from "../../components/PasswordReset/index.tsx";
import Register from "../../components/Register/index.tsx";
import Terms from "../../components/Terms/index.tsx";
import { LoginData } from "../../components/Login/Login.types";
import { RegisterData } from "../../components/Register/Register.types";
import { getAppSettings } from "../../utils/appSettings.tsx";
import { useTheme } from "../../context/Theme.context.tsx";
import {
  authActivateAccount,
  authForgotPassword,
  authLogin,
  authRegister,
  authResetPassword,
  authValidateCode,
} from "../../services/auth.services.tsx";

export const LoginContext = createContext<LoginContextType | undefined>(undefined);

export const LoginContextProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { theme } = useTheme();

  // States
  const [currentView, setCurrentView] = useState<ViewName>("login");
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [resetUuid, setResetUuid] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [registerToken, setRegisterToken] = useState("");

  // Handlers
  const onLogin = async (formData: LoginData) => {
    try {
      setIsLoading(true);

      const token = await authLogin(formData);

      localStorage.setItem("token", token);

      navigate("/home");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "API Error";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onForgot = async (email: string) => {
    try {
      setIsLoading(true);

      const uuid = await authForgotPassword(email);

      setResetUuid(uuid);
      setEmail(email);
      setCurrentView("otpPassword");

      toast.success("Verification code sent successfully");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "API Error";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onOtpPassword = async (otp: string) => {
    try {
      setIsLoading(true);

      const token = await authValidateCode(resetUuid, otp);

      setResetToken(token);

      setCurrentView("passwordReset");

      toast.success("Verification code validated successfully");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "API Error";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordReset = async (newPassword: string) => {
    try {
      setIsLoading(true);

      await authResetPassword(resetToken, newPassword);

      setCurrentView("login");

      toast.success("Your password has been changed successfully");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "API Error";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onRegister = async (data: RegisterData) => {
    try {
      setIsLoading(true);

      const token = await authRegister(data);

      setRegisterToken(token);
      setEmail(data.email);

      setCurrentView("otpRegister");

      toast.success("Verification code sent successfully");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "API Error";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onOtpRegister = async (otp: string) => {
    try {
      setIsLoading(true);

      await authActivateAccount(registerToken, otp);

      setCurrentView("login");

      toast.success("Your account has been created successfully");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "API Error";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onResendCode = async () => {
    try {
      setIsLoading(true);

      const uuid = await authForgotPassword(email);

      setResetUuid(uuid);

      toast.success("Verification code resent successfully.");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "API Error";
      toast.error(`Error: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onBackToLogin = () => {
    setCurrentView("login");
  };

  // Rendering
  const renderView = () => {
    switch (currentView) {
      case "login":
        return <Login />;

      case "forgot":
        return <Forgot />;

      case "otpPassword":
        return <OtpPassword />;

      case "otpRegister":
        return <OtpRegister />;

      case "passwordReset":
        return <PasswordReset />;

      case "register":
        return <Register />;

      case "terms":
        return <Terms onBackToLogin={onBackToLogin} />;

      default:
        return null;
    }
  };

  // Others
  const spinnerVariant = theme === "dark" ? "light" : "dark";

  const loadingComponent = (
    <div className="d-flex justify-content-center my-5">
      <Spinner variant={spinnerVariant} />
    </div>
  );

  // useEffects
  useEffect(() => {
    const settings = getAppSettings();
    i18n.changeLanguage(settings.locale);
  }, []);

  // Provider
  return (
    <LoginContext.Provider
      value={{
        currentView,
        setCurrentView,
        isLoading,
        loadingComponent,
        email,
        renderView,
        onLogin,
        onForgot,
        onOtpPassword,
        onResendCode,
        onOtpRegister,
        onPasswordReset,
        onRegister,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};

export const useLoginContext = () => {
  const context = useContext(LoginContext);

  if (!context) {
    throw new Error("useLogin must be used inside a LoginContextProvider");
  }

  return context;
};
